import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import style from "./style.module.scss";
import DropZone from "../image-drop-zone";
import PhotoLibraryPicker from "../photo-library-picker";
import { FileButton } from "../../../ui";
import { useUploadWithProgress, type UploadResult } from "../../../hooks";
import { useUser } from "../../../data/context/auth-context";
import { mediaByProductQueryOptions } from "../../../data/queries/media";

interface UploadImageProps {
  className?: string;
  productId: string;
  userId?: string;
  onChange?: (url: string) => void;
  onPreviewChange?: (url: string | undefined) => void;
}

function UploadImage({
  className,
  productId,
  userId,
  onChange,
  onPreviewChange,
}: UploadImageProps) {
  const user = useUser();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Use provided userId (for admin editing) or fall back to current user
  const targetUserId = userId || user?.uid;

  // Fetch media to check if photos exist
  const { data: media = [] } = useQuery({
    ...mediaByProductQueryOptions(productId, targetUserId || ""),
    enabled: !!productId && !!targetUserId,
  });

  const hasPhotos = media.length > 0;

  const { uploads, uploadFiles, isUploading } = useUploadWithProgress({
    productId,
    userId: targetUserId || "",
  });

  // Get current upload progress and preview
  const currentUpload = Object.values(uploads).find(
    (u) => u.status === "uploading" || u.status === "pending",
  );
  const progress = currentUpload?.progress || 0;
  const previewUrl = currentUpload?.previewUrl;

  // Notify parent of preview changes (for custom preview rendering)
  useEffect(() => {
    onPreviewChange?.(previewUrl);
  }, [previewUrl, onPreviewChange]);

  const onSelectFiles = async (acceptedFiles: File[]) => {
    if (!targetUserId || isUploading) {
      return;
    }

    const results = await uploadFiles(acceptedFiles);

    // Call onChange with the first successful upload URL
    if (onChange) {
      const firstSuccess = results.find(
        (r): r is PromiseFulfilledResult<UploadResult> =>
          r.status === "fulfilled" && r.value?.success === true,
      );
      if (firstSuccess?.value?.success && firstSuccess.value.media?.url) {
        onChange(firstSuccess.value.media.url);
      }
    }
  };

  const handleClick = () => {
    if (isUploading) return;

    if (hasPhotos) {
      setIsPickerOpen(true);
    }
    // If no photos, FileButton will handle file picker
  };

  const handlePhotoSelect = (url: string) => {
    onChange?.(url);
  };

  // If parent handles preview, don't show it here
  const showPreview = previewUrl && !onPreviewChange;

  // If photos exist, clicking opens picker modal
  if (hasPhotos) {
    return (
      <>
        <div onClick={handleClick} className={style.container}>
          {showPreview && <img src={previewUrl} alt="" className={style.preview} />}
          <DropZone
            loading={isUploading}
            progress={progress}
            className={className}
            dark={!!previewUrl}
          />
        </div>
        <PhotoLibraryPicker
          open={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          onSelect={handlePhotoSelect}
          onPreviewChange={onPreviewChange}
          productId={productId}
          userId={targetUserId || ""}
        />
      </>
    );
  }

  // No photos - use direct file picker
  return (
    <FileButton
      onSelect={onSelectFiles}
      accept="image/*"
      multiple
      render={({ onClick }) => (
        <div onClick={isUploading ? undefined : onClick} className={style.container}>
          {showPreview && <img src={previewUrl} alt="" className={style.preview} />}
          <DropZone
            loading={isUploading}
            progress={progress}
            className={className}
            dark={!!previewUrl}
            onDrop={isUploading ? undefined : onSelectFiles}
          />
        </div>
      )}
    />
  );
}

export default UploadImage;
